import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Phone,
  TrendingUp,
  Headphones,
  Calendar,
  DollarSign,
  Star,
  Bell,
  FileText,
  RefreshCw,
  Plus,
  Search,
  Sparkles,
  Check,
  ArrowRight,
  Bot,
  Building2
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

const CATEGORY_ICONS = {
  receptionist: Phone,
  sales: TrendingUp,
  support: Headphones,
  appointment: Calendar,
  collections: DollarSign,
  survey: Star,
  reminder: Bell,
  intake: FileText,
};

export default function AgentTemplates() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [customization, setCustomization] = useState({
    agent_name: "",
    business_name: "",
    business_info: "",
    business_hours: "9 AM - 5 PM, Monday to Friday",
    business_type: "",
  });

  useEffect(() => {
    loadData();
  }, [selectedCategory, selectedIndustry]);

  const loadData = async () => {
    setLoading(true);
    
    try {
      let url = `${API_URL}/api/agent-templates/templates`;
      const params = new URLSearchParams();
      if (selectedCategory !== "all") params.append("category", selectedCategory);
      if (selectedIndustry !== "all") params.append("industry", selectedIndustry);
      if (params.toString()) url += `?${params.toString()}`;
      
      const [templatesRes, categoriesRes, industriesRes] = await Promise.all([
        fetch(url),
        fetch(`${API_URL}/api/agent-templates/categories`),
        fetch(`${API_URL}/api/agent-templates/industries`)
      ]);
      
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data.categories || []);
      }
      if (industriesRes.ok) {
        const data = await industriesRes.json();
        setIndustries(data.industries || []);
      }
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (template) => {
    setSelectedTemplate(template);
    setCustomization({
      agent_name: template.name,
      business_name: user?.business_name || "",
      business_info: "",
      business_hours: "9 AM - 5 PM, Monday to Friday",
      business_type: user?.industry || "",
    });
    setIsCreateDialogOpen(true);
  };

  const createAgent = async () => {
    if (!customization.agent_name) {
      toast.error("Agent name is required");
      return;
    }
    
    setCreating(true);
    const token = localStorage.getItem("speakr_token");
    
    try {
      const res = await fetch(`${API_URL}/api/agent-templates/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          business_id: user?.business_id,
          customization
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Agent "${customization.agent_name}" created successfully!`);
        setIsCreateDialogOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create agent");
      }
    } catch (error) {
      toast.error("Failed to create agent");
    } finally {
      setCreating(false);
    }
  };

  const filteredTemplates = Array.isArray(templates) ? templates.filter(t => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      t.name.toLowerCase().includes(query) ||
      t.description.toLowerCase().includes(query)
    );
  }) : [];

  const getCategoryIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || Bot;
    return Icon;
  };

  if (loading && templates.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-blue-500" />
            AI Agent Templates
          </h1>
          <p className="text-slate-500 mt-1">Choose a pre-built agent template and customize it for your business</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-sm text-slate-500">Category:</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name} ({cat.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Label className="text-sm text-slate-500">Industry:</Label>
          <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {industries.map(ind => (
                <SelectItem key={ind.id} value={ind.id}>{ind.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Category Quick Filters */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={selectedCategory === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedCategory("all")}
        >
          All
        </Button>
        {categories.map(cat => {
          const Icon = getCategoryIcon(cat.id);
          return (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className="flex items-center gap-1"
            >
              <Icon className="w-4 h-4" />
              {cat.name}
            </Button>
          );
        })}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map(template => {
          const Icon = getCategoryIcon(template.category);
          
          return (
            <Card 
              key={template.id}
              className="hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => openCreateDialog(template)}
              data-testid={`template-card-${template.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100`}>
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {template.category}
                  </Badge>
                </div>
                <CardTitle className="mt-3 group-hover:text-blue-600 transition-colors">
                  {template.name}
                </CardTitle>
                <CardDescription className="line-clamp-2">
                  {template.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="flex flex-wrap gap-1">
                  {template.features?.slice(0, 4).map((feature, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {feature.replace(/_/g, " ")}
                    </Badge>
                  ))}
                  {template.features?.length > 4 && (
                    <Badge variant="secondary" className="text-xs">
                      +{template.features.length - 4}
                    </Badge>
                  )}
                </div>
                
                {template.industries && !template.industries.includes("all") && (
                  <div className="mt-3 flex items-center gap-1 text-sm text-slate-500">
                    <Building2 className="w-3 h-3" />
                    <span>{template.industries.slice(0, 3).join(", ")}</span>
                  </div>
                )}
              </CardContent>
              
              <CardFooter>
                <Button className="w-full group-hover:bg-blue-600 transition-colors">
                  <Plus className="w-4 h-4 mr-2" />
                  Use Template
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <Bot className="w-12 h-12 mx-auto text-slate-300" />
          <p className="mt-4 text-slate-500">No templates found matching your criteria</p>
          <Button variant="outline" className="mt-4" onClick={() => {
            setSelectedCategory("all");
            setSelectedIndustry("all");
            setSearchQuery("");
          }}>
            Clear Filters
          </Button>
        </div>
      )}

      {/* Create Agent Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              Create {selectedTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Customize this template for your business
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="agent_name">Agent Name *</Label>
                <Input
                  id="agent_name"
                  value={customization.agent_name}
                  onChange={(e) => setCustomization(prev => ({ ...prev, agent_name: e.target.value }))}
                  placeholder="My Sales Agent"
                  data-testid="agent-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name</Label>
                <Input
                  id="business_name"
                  value={customization.business_name}
                  onChange={(e) => setCustomization(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder="Your company name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="business_type">Industry/Business Type</Label>
                <Select
                  value={customization.business_type}
                  onValueChange={(value) => setCustomization(prev => ({ ...prev, business_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.filter(i => i.id !== "all").map(ind => (
                      <SelectItem key={ind.id} value={ind.id}>{ind.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_hours">Business Hours</Label>
                <Input
                  id="business_hours"
                  value={customization.business_hours}
                  onChange={(e) => setCustomization(prev => ({ ...prev, business_hours: e.target.value }))}
                  placeholder="9 AM - 5 PM, Mon-Fri"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="business_info">Business Description</Label>
              <Textarea
                id="business_info"
                value={customization.business_info}
                onChange={(e) => setCustomization(prev => ({ ...prev, business_info: e.target.value }))}
                placeholder="Brief description of your business, services, and key information the agent should know..."
                rows={4}
              />
            </div>
            
            {selectedTemplate && (
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-sm text-slate-700 mb-2">Template Features</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-1 text-sm text-slate-600">
                      <Check className="w-3 h-3 text-emerald-500" />
                      {feature.replace(/_/g, " ")}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createAgent} disabled={creating} data-testid="create-agent-btn">
              {creating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Create Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
